// import { useRef, useState } from "react"; // for the hidden walkthrough video
import { Link } from "react-router-dom";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import Reveal from "../../components/Reveal";

// Tech stack band data, unused while that section is hidden below.
// const PLATFORMS = [
//   ["📱", "Mobile App", "Flutter · SQLite / Drift", "Android-first app for parents, teachers, and therapists. Handles day-to-day communication, journal entries, voice notes, and media capture, all offline-first with a local sync queue."],
//   ["💻", "Web / Admin PWA", "React · TypeScript · Vite", "Progressive web app for head teachers and school admins. Handles student review, approvals, resource management, reporting, and oversight, accessible on any school desktop browser."],
//   ["☁", "Backend", "Node.js · Supabase · PostgreSQL", "Supabase-backed persistence with row-level security, role-scoped data access, and a local development API. Sync resolves conflicts using last-write-wins on updated_at."],
// ];

const ROLES = [
  ["Parents", "See only their linked child's progress, receive messages from teachers, and view journal entries and home activity assignments, all in their language preference."],
  ["Teachers", "Log student observations, update IEP goal progress, send messages to parents, and assign home activities, directly from the mobile app, offline."],
  ["Therapists", "Record therapy session notes, assign home practice, manage caseloads across multiple schools, and share resources with the classroom team."],
  ["School Admins", "Manage school membership, review and approve staff and student enrolments, publish announcements, and generate term summary reports from the web PWA."],
];

const FEATURES = [
  "Student profiles: structured records with disability category, current year, and linked staff.",
  "IEP goal tracking: create, assign, and update Individualised Education Plan goals with progress notes.",
  "Teacher-parent messaging: threaded, school-scoped conversations that sync across devices once connectivity is available.",
  "Photo & video journal: media captured in school with per-item parent consent controls before sharing.",
  "Therapy session notes: structured notes linked to goals, visible to the full care team for each student.",
  "Home activity assignments: therapists assign activities for parents to practise at home, with completion feedback loops.",
  "Offline write queue: every action written locally first; a sync-status indicator shows what is pending and confirmed.",
  "WhatsApp bridge (planned): notifications reach parents on the platform they already use, for schools without smartphones.",
  "Twi & Ewe interface (Phase 3): the full admin and parent interface localised into Ghanaian languages.",
  "School admin dashboard: approval queues, student summary counts, resource library, and term reporting.",
];

const SERVES = [
  ["👤", "Students with Special Needs", "Their progress, goals, and care plans travel with them, structured, visible, and never lost in a notebook."],
  ["👪", "Families", "Parents stay informed and engaged without needing a data plan or a smartphone, WhatsApp bridge brings updates to where they already are."],
  ["🎓", "Teachers & Therapists", "Less time on paperwork, more time on the child. Every note, every goal update, every home activity, logged once and visible to the whole team."],
  ["🏫", "Schools", "A free, deployable tool for special needs schools across Ghana, no IT budget, no server maintenance, works on the devices already in school."],
];

export default function ProjectKasa() {
  // Walkthrough video state, unused while that section is hidden below.
  // const videoRef = useRef(null);
  // const [videoReady, setVideoReady] = useState(false);

  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pt-32 md:px-12 md:pt-40">
        <Link to="/projects" className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint hover:text-heading">
          &larr; Back to Projects
        </Link>
      </div>

      <Reveal className="mx-auto max-w-content px-6 pb-14 pt-8 md:px-12 md:pb-20">
        <p className="eyebrow">PWA &amp; Mobile &middot; Special Needs Ed-Tech &middot; Offline-First</p>
        <h1 className="mt-3 font-serif text-4xl font-extrabold italic leading-tight text-heading md:text-5xl">KASA</h1>
        <p className="mt-2 font-serif italic text-gold-500">
          Akan for <span className="not-italic">conversation</span>, the bridge between school and home
        </p>
        <p className="mt-6 max-w-[62ch] text-[1rem] leading-relaxed text-ink-soft">
          An offline-first collaboration platform connecting <strong className="font-medium text-heading">parents,
          teachers, therapists, and school admins</strong> around students with special needs in Ghanaian schools.
          Built for low-bandwidth, low-connectivity environments, so no child&rsquo;s progress gets lost when the
          internet drops.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <a
            href="https://kasa.ellenboakye.online"
            target="_blank"
            rel="noreferrer"
            className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            Live Site
          </a>
          <a
            href="https://github.com/ellenboakye/kasa"
            target="_blank"
            rel="noreferrer"
            className="text-[0.75rem] uppercase tracking-widest2 text-ink-soft hover:text-heading"
          >
            GitHub
          </a>
        </div>
      </Reveal>

      {/* Tech stack band, hidden for now (keep the stack off the public page).
      <Reveal as="section" className="border-y border-line bg-surface-alt">
        <div className="mx-auto grid max-w-content gap-px border-x border-line bg-line px-6 sm:grid-cols-3 md:px-12">
          {PLATFORMS.map(([icon, name, stack, desc]) => (
            <div key={name} className="bg-surface-alt px-6 py-8 first:pl-0 sm:first:pl-6">
              <div className="text-xl">{icon}</div>
              <div className="mt-3 font-serif text-lg text-heading">{name}</div>
              <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-gold-500">{stack}</div>
              <p className="mt-3 text-[0.85rem] leading-relaxed text-ink-soft">{desc}</p>
            </div>
          ))}
        </div>
      </Reveal>
      */}

      {/* Walkthrough video, hidden until public/projects/kasa.mp4 exists
          (without it this showed a placeholder with a developer note).
          To restore: add the video, uncomment this and the videoRef /
          videoReady state above.
      <section className="border-b border-line">
        <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-20">
          <p className="eyebrow">Project Walkthrough</p>
          <h2 className="mt-2 font-serif text-2xl text-heading">See it in action</h2>
          <div className="relative mt-8 aspect-video border border-line bg-surface-alt">
            <video
              ref={videoRef}
              controls
              src="/projects/kasa.mp4"
              className="h-full w-full object-cover"
              style={{ display: videoReady ? "block" : "none" }}
              onLoadedData={() => setVideoReady(true)}
            />
          </div>
        </div>
      </section>
      */}

      <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
        <div className="grid gap-14 md:grid-cols-[1fr_320px] md:gap-16">
          <div>
            <Reveal className="mb-16">
              <p className="eyebrow">Overview</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">The problem it solves</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                When a child with special needs attends school in Ghana, their progress lives in fragments, a
                teacher&rsquo;s notebook, a therapist&rsquo;s session file, a parent&rsquo;s memory of what was
                said at the last gate meeting. Nothing is shared in real time. When a student moves school, the
                record often disappears entirely.
              </p>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                KASA creates one shared, structured space where a student&rsquo;s IEP goals, therapy notes,
                journal entries, and progress cards are visible to every adult responsible for that child, and
                always accessible offline, because school labs and parents&rsquo; phones rarely have reliable
                data connections.
              </p>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">Users</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">Who it serves</h2>
              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                {ROLES.map(([name, desc]) => (
                  <div key={name} className="border-t border-line pt-4">
                    <p className="font-serif text-base text-heading">{name}</p>
                    <p className="mt-2 text-[0.85rem] leading-relaxed text-ink-soft">{desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">Features</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">What it does</h2>
              <ul className="mt-6 divide-y divide-line border-t border-line">
                {FEATURES.map((f) => (
                  <li key={f} className="flex gap-3 py-3.5 text-[0.9rem] leading-relaxed text-ink-soft">
                    <span className="mt-[0.6em] h-1 w-1 flex-shrink-0 rounded-full bg-gold-500" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <Reveal as="aside" className="h-fit space-y-6 md:sticky md:top-28">
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Project Info</p>
              <dl className="space-y-3 text-[0.85rem]">
                <div><dt className="text-ink-faint">Type</dt><dd className="text-heading">Monorepo, Web PWA + Mobile + API</dd></div>
                <div><dt className="text-ink-faint">Target</dt><dd className="text-heading">Special needs schools in Ghana</dd></div>
                <div><dt className="text-ink-faint">Status</dt><dd className="text-heading">Phase 0 complete, active development</dd></div>
              </dl>
            </div>
            {/* Key Services (tech stack), hidden for now.
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Key Services</p>
              <p className="text-[0.85rem] leading-relaxed text-ink-soft">
                Supabase (auth, Postgres, RLS, storage) &middot; SQLite / Drift &middot; Vite &middot; WhatsApp Bridge (planned)
              </p>
            </div>
            */}
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Links</p>
              <a href="https://kasa.ellenboakye.online" target="_blank" rel="noreferrer" className="block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading">
                kasa.ellenboakye.online &rarr;
              </a>
              <a href="https://github.com/ellenboakye/kasa" target="_blank" rel="noreferrer" className="mt-3 block text-[0.75rem] uppercase tracking-widest2 text-link hover:text-heading">
                View on GitHub &rarr;
              </a>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal as="section" className="bg-forest-900">
        <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
          <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">Impact</p>
          <h2 className="mt-2 font-serif text-2xl font-extrabold text-white md:text-3xl">Who benefits</h2>
          <div className="mt-10 grid gap-px border-y border-white/15 sm:grid-cols-2 lg:grid-cols-4">
            {SERVES.map(([icon, who, what], i) => (
              <div
                key={who}
                className={`border-t border-white/15 py-6 sm:border-t-0 ${i % 2 === 0 ? "sm:pr-6" : "sm:pl-6"} lg:px-6 lg:first:pl-0 lg:last:pr-0`}
              >
                <div className="text-xl">{icon}</div>
                <div className="mt-3 font-serif text-lg text-white">{who}</div>
                <p className="mt-2 text-[0.85rem] leading-relaxed text-forest-50/75">{what}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <section className="border-t border-line">
        <div className="mx-auto flex max-w-content flex-wrap items-center justify-between gap-6 px-6 py-14 md:px-12">
          <p className="font-serif text-xl text-heading">See all projects</p>
          <Link
            to="/projects"
            className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            Browse Projects &rarr;
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
